/**
 * CalculateApplicationContainer
 * --
 */
import React, { useState, useEffect } from 'react';
import CalculateApplicationPresenter from './CalculateApplicationPresenter';
import moment from 'moment';
import MessageAlert, { getCookie } from 'utils';
import { SettlementAmountApi, WithdrawApi, ShopInformationApi } from 'api';
import { SETTLEMENT_STATUS } from 'constants/SettlementConstants';
import { useUserStack } from 'store';
import { useAuthCheck } from 'hooks/AuthHooks';

/* 데이터 초기 설정 */
// 날짜포맷
const MOMENT_FORMAT = 'YYYY-MM-DD';
// 날짜지정
const initialDate = {
  year: moment().year(),
  month: moment().month() + 1,
  startDate: moment().startOf('month').format(MOMENT_FORMAT),
  endDate: moment().endOf('month').format(MOMENT_FORMAT),
};

const initialDataWithdraw = {
  withdraw_id: null,
  is_tax_bill: true, // Y, N
  bank_name: '',
  account_number: '',
  withdraw_status: 'APPLY',
  withdrawal_manager: '-', //출금담당자(JM)
  created_at: null,
  updated_at: null,
  shop_information_id: null,
  settlement_amount_ids: [],
  etc: '',
};

/**----------------------------------------
 * [Component] 정산등록 - 컨테이너
 * ----------------------------------------
 */
const CalculateApplicationContainer = ({
  shopId,
  defaultShopId,
  permission,
}) => {
  /* ===== MUTATE ===== */
  const { authCheck } = useAuthCheck();

  /* ===== VARIABLE ===== */

  const cookie = getCookie('JM_COFFEE_ACCESS_TOKEN');

  /* ===== INIT ===== */
  const userInfo = useUserStack();
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [settlementList, setSettlementList] = useState([]);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [selectedList, setSelectedList] = useState([]);
  const [shopInformation, setShopInformation] = useState(null);
  const [withdrawData, setWithdrawData] = useState(initialDataWithdraw);
  const [withdrawList, setWithdrawList] = useState(null);
  const [withdrawListModal, setWithdrawListModal] = useState(false);
  const [withdrawApplicationModal, setWithdrawApplicationModal] =
    useState(false);
  const [modalType, setModalType] = useState('WITHDRAW_APPLICATION');
  const [stopAuth, setStopAuth] = useState(false);
  const [authInfo, setAuthInfo] = useState(null);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 어드민 권한 확인 함수
   */
  const handleTokenAuth = async () => {
    if (cookie) {
      const { status, data } = await authCheck();
      if (status !== 200) return;
      return data;
    }
  };
  const onChangeWithdrawData = (key, value) => {
    setWithdrawData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  /**--------------------
   * 월 증감
   * --------------------
   */
  const handleChangeMonth = (arrow) => {
    let newMonth = currentDate.month + (arrow === 'left' ? -1 : 1);
    let month, year;

    if (newMonth < 1) {
      month = 12;
      year = currentDate.year - 1;
    } else if (newMonth > 12) {
      month = 1;
      year = currentDate.year + 1;
    } else {
      month = newMonth;
      year = currentDate.year;
    }

    // 예외처리
    if (initialDate.month < month && initialDate.year <= year) {
      return MessageAlert.warning(
        `${initialDate.month}월이 최신 정산월입니다.`
      );
    }
    const newData = {
      month,
      year,
      startDate: moment(`${year}-${month}`)
        .startOf('month')
        .format(MOMENT_FORMAT),
      endDate: moment(`${year}-${month}`).endOf('month').format(MOMENT_FORMAT),
    };
    setCurrentDate(newData);
  };

  /**--------------------
   * 정산처리 목록 조회
   * --------------------
   */
  const getCalculateList = async () => {
    setLoading(true);
    try {
      const { status, message, data } =
        await SettlementAmountApi.getSettlementAmounts({
          shop_information_id: currentShop,
          start_date: moment(currentDate.startDate).unix(),
          end_date: moment(currentDate.endDate).unix(),
        });
      if (status !== 200) throw { message };
      setTimeout(() => {
        setSettlementList(data);
        setLoading(false);
      }, 500);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 정산 상태 일괄 변경
   * --------------------
   */
  const handleChangeStatusAll = async () => {
    if (
      !window.confirm(
        `${settlementList.length}개의 출금처리를 일괄로 진행하시겠습니까?`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const updateTime = moment().unix();
      const newData = settlementList.map((item) => ({
        settlement_amount_id: item.settlement_amount_id,
        settlement_amount_status: item.settlement_amount_status,
        updated_at: updateTime,
        settlement_amount_status: 'DECIDED', // 출금완료상태로 변경
      }));
      // API
      const { status, message } =
        await SettlementAmountApi.upsertSettlementAmount(newData);
      if (status !== 200) throw { message };

      setTimeout(() => {
        MessageAlert.info(`일괄 [출금완료]상태로 변경되었습니다.`);
        getCalculateList();
        // setLoading(false);
      }, 1000);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 정산상태 변경
   * --------------------
   */
  const handleChangeAmountStatus = async (newStatus) => {
    if (!selectedList.length)
      return MessageAlert.warning('변경할 목록을 선택해주세요');

    const statusTxt = SETTLEMENT_STATUS[newStatus].title;
    //
    if (!window.confirm(`정산상태를 [${statusTxt}]로 변경하시겠습니까?`)) {
      return;
    }
    setLoading(true);
    try {
      const newData = selectedList.map((item) => ({
        settlement_amount_id: item.settlement_amount_id,
        settlement_amount_status: newStatus,
      }));

      // API
      const { status, message } =
        await SettlementAmountApi.upsertSettlementAmount(newData);
      if (status !== 200) throw { message };

      // 데이터 변경
      getCalculateList();
      setTimeout(() => {
        MessageAlert.info(`[${statusTxt}]상태로 변경되었습니다.`);
        setLoading(false);
      }, 500);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 업체정보 조회
   * --------------------
   */
  const getShopInformation = async () => {
    setLoading(true);
    try {
      const { status, message, data } =
        await ShopInformationApi.getShopInformations({
          shop_information_id: currentShop,
          join_tables: `["shop_information_file"]`,
        });
      if (status !== 200) throw { message };

      if (data) {
        setShopInformation(data ? data[0] : null);
        setWithdrawData((prev) => ({
          ...prev,
          bank_name: data[0].mall_information?.bank_name,
          account_number: data[0].mall_information?.account_number,
          shop_information_id: currentShop,
          ...(permission === 'ADMIN' && {
            withdrawal_manager: userInfo.admin_name,
            withdraw_status: 'APPROVE',
          }),
        }));
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 리셋 함수
   * --------------------
   */
  const handleReset = () => {
    setWithdrawApplicationModal(false);
    getWithdrawList();
    setWithdrawData({
      ...initialDataWithdraw,
      ...(permission === 'ADMIN' && {
        withdrawal_manager: userInfo.admin_name,
        withdraw_status: 'APPROVE',
      }),
    });
  };

  /**--------------------
   * 출금내역 조회
   * --------------------
   */
  const getWithdrawList = async () => {
    setLoading(true);
    try {
      const { status, message, data } = await WithdrawApi.getWithdraws({
        shop_information_id: currentShop,
        start_date: moment(currentDate.startDate).unix(),
        end_date: moment(currentDate.endDate).unix(),
        join_tables: `["shop_information"]`,
      });
      if (status !== 200) throw { message };
      setWithdrawList(data.toSorted((a, b) => b.withdraw_id - a.withdraw_id));
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 출금완료처리
   * --------------------
   */
  const onWithdrawComplete = async (permission) => {
    /* 
      is_tax_bill: true, // Y, N
      bank_name: '',
      account_number: '',
      withdraw_status: 'APPROVE',
      withdrawal_manager: '', //출금담당자(JM)
      created_at: null,
      updated_at: null,
      shop_information_id: null,
      settlement_amount_ids: [],
      etc: '',
    */
    const permissionValue = {
      ADMIN: {
        msg: '출금처리를 진행하시겠습니까?',
        withdraw_status: '',
      },
      SHOP_ADMIN: {
        msg: '출금신청을 진행하시겠습니까?',
        withdraw_status: '',
      },
    };
    /* 예외처리 */
    if (!window.confirm(permissionValue[permission]?.msg)) {
      return;
    }
    if (!withdrawData.withdrawal_manager) {
      return MessageAlert.warning('출금 담당자명을 입력해주세요');
    }
    if (!withdrawData.bank_name || !withdrawData.account_number) {
      return MessageAlert.warning('계좌정보를 입력해주세요');
    }
    // setLoading(true);
    try {
      /* 데이터 만들기 */
      const is_tax_bill = withdrawData.is_tax_bill === true ? 'Y' : 'N';
      const currentTime = moment().unix();
      const withdraw_price =
        !selectedList || selectedList.length < 1
          ? 0
          : selectedList.reduce(
              (acc, cur) => (acc += cur.settlement_amount_price ?? 0),
              0
            );
      const newData = {
        ...withdrawData,
        is_tax_bill,
        withdraw_price,
        created_at: currentTime,
        updated_at: currentTime,
        settlement_amount_ids: selectedList.map(({ key }) => key),
      };

      /* API */
      const { status, message, data } = await WithdrawApi.createWithdraw(
        newData
      );
      if (status !== 200) throw { message };
      setTimeout(() => {
        MessageAlert.success('출금처리 완료되었습니다.');
        handleReset();
        setLoading(false);
      }, 850);
      /* 후처리 */
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };
  /**--------------------
   * 출금정보 수정
   * --------------------
   */
  const handleUpdateWithdraw = async () => {
    setLoading(true);
    try {
      if (!withdrawData.withdraw_id) {
        return MessageAlert.warning('출금 완료할 정보를 찾을 수 없습니다.');
      }

      if (!window.confirm('출금완료 처리하시겠습니까?')) {
        return;
      }

      const newData = {
        withdraw_id: withdrawData.withdraw_id,
        withdrawal_manager: withdrawData.withdrawal_manager,
        withdraw_status: 'APPROVE',
      };
      const { status, message } = await WithdrawApi.updateWithdraw(newData);
      if (status !== 200) throw { message };
      MessageAlert.success('출금처리 완료되었습니다.');
      handleReset();
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 출금정보 삭제
   * --------------------
   */
  const handleDeleteWithdraw = async (idx) => {
    setLoading(true);
    try {
      if (!idx) {
        return MessageAlert.warning('삭제할 정보를 찾을 수 없습니다.');
      }

      if (
        !window.confirm(
          '정말 출금정보를 삭제하시겠습니까?\n삭제되면 데이터를 복구할 수 없습니다.'
        )
      ) {
        return;
      }

      const { status, message } = await WithdrawApi.deleteWithdraw(idx);
      if (status !== 200) throw { message };
      MessageAlert.success('출금 정보가 삭제되었습니다.');
      handleReset();
      setLoading(false);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };
  /* ===== HOOKS ===== */
  useEffect(() => {
    if (stopAuth || authInfo) return;
    const fetchAuth = async () => {
      const result = await handleTokenAuth();
      setAuthInfo(result);
      setStopAuth(true);
    };
    fetchAuth();
  }, []);
  /**--------------------
   * 업체 변경 시 실행
   * --------------------
   */
  useEffect(() => {
    currentShop && getCalculateList();
    currentShop && setSelectedList([]);
    currentShop && getWithdrawList();
    currentShop && getShopInformation();
  }, [currentShop, currentDate]);
  // useEffect(() => {
  //   getCalculateList();
  // }, [currentDate]);

  /* ===== RENDER ===== */
  // Variables
  const childProps = {
    permission,
    //
    loading,
    settlementList,
    //
    currentShop,
    setCurrentShop,
    //
    currentDate,
    setCurrentDate,
    /* 정산 연/월 변경함수 */
    onChangeMonth: handleChangeMonth,
    // 정산상태 일괄 변경
    onChangeAmountStatus: handleChangeAmountStatus,
    // 일괄 출금상태 변경 함수
    onChangeStatusAll: handleChangeStatusAll,
    onUpdateWithdraw: handleUpdateWithdraw,
    onDeleteWithdraw: handleDeleteWithdraw,
    selectedList,
    setSelectedList,
    // 출금목록
    withdrawList,
    withdrawListModal,
    setWithdrawListModal,
    modalType,
    setModalType,

    // 업체정보
    shopInformation,
    // 출금처리버튼
    withdrawApplicationModal,
    setWithdrawApplicationModal,

    withdrawData,
    onChangeWithdrawData,
    onWithdrawComplete,
    authInfo: authInfo,
  };

  // console.log('selectedList: ', selectedList);

  // VIEW
  return (
    <>
      <CalculateApplicationPresenter {...childProps} />
    </>
  );
};

/* DF */
CalculateApplicationContainer.defaultProps = {
  shopId: null,
  defaultShopId: null,
  permission: 'ADMIN',
};
export default CalculateApplicationContainer;
